import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// =============================================
// ASYNC THUNKS
// =============================================

// Fetch all delegations for store (managers/owners only)
export const fetchDelegations = createAsyncThunk(
  'delegations/fetchAll',
  async ({ status, page, limit } = {}) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    const res = await axios.get(`/delegations?${params.toString()}`);
    return res.data;
  }
);

// Fetch delegations I've granted
export const fetchGrantedDelegations = createAsyncThunk(
  'delegations/fetchGranted',
  async ({ status, includeExpired = false } = {}) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (includeExpired) params.append('includeExpired', 'true');
    const res = await axios.get(`/delegations/granted?${params.toString()}`);
    return res.data;
  }
);

// Fetch delegations I've received
export const fetchReceivedDelegations = createAsyncThunk(
  'delegations/fetchReceived',
  async ({ status, activeOnly = true } = {}) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('activeOnly', activeOnly.toString());
    const res = await axios.get(`/delegations/received?${params.toString()}`);
    return res.data;
  }
);

// Fetch pending delegations requiring approval
export const fetchPendingDelegations = createAsyncThunk(
  'delegations/fetchPending',
  async () => {
    const res = await axios.get('/delegations/pending');
    return res.data;
  }
);

// Fetch delegation statistics
export const fetchDelegationStats = createAsyncThunk(
  'delegations/fetchStats',
  async () => {
    const res = await axios.get('/delegations/stats');
    return res.data;
  }
);

// Fetch permissions I can delegate
export const fetchDelegablePermissions = createAsyncThunk(
  'delegations/fetchDelegablePermissions',
  async () => {
    const res = await axios.get('/delegations/delegable-permissions');
    return res.data;
  }
);

// Create a new delegation
export const createDelegation = createAsyncThunk(
  'delegations/create',
  async (delegationData, { rejectWithValue }) => {
    try {
      const res = await axios.post('/delegations', delegationData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create delegation' });
    }
  }
);

// Approve a pending delegation
export const approveDelegation = createAsyncThunk(
  'delegations/approve',
  async (delegationId, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/delegations/${delegationId}/approve`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to approve delegation' });
    }
  }
);

// Reject a pending delegation
export const rejectDelegation = createAsyncThunk(
  'delegations/reject',
  async ({ delegationId, reason }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/delegations/${delegationId}/reject`, { reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to reject delegation' });
    }
  }
);

// Revoke an active delegation
export const revokeDelegation = createAsyncThunk(
  'delegations/revoke',
  async ({ delegationId, reason }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/delegations/${delegationId}/revoke`, { reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to revoke delegation' });
    }
  }
);

// =============================================
// SLICE
// =============================================

const delegationsSlice = createSlice({
  name: 'delegations',
  initialState: {
    // All delegations (for managers/owners)
    delegations: [],
    pagination: null,
    // Delegations I've granted
    grantedDelegations: [],
    // Delegations I've received
    receivedDelegations: [],
    // Pending delegations requiring approval
    pendingDelegations: [],
    // Permissions I can delegate
    delegablePermissions: [],
    // Statistics
    stats: null,
    // Status
    status: 'idle',
    createStatus: 'idle',
    approveStatus: 'idle',
    error: null
  },
  reducers: {
    clearDelegationError: (state) => {
      state.error = null;
    },
    resetDelegationStatuses: (state) => {
      state.createStatus = 'idle';
      state.approveStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all delegations
      .addCase(fetchDelegations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDelegations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.delegations = action.payload.delegations || [];
        state.pagination = action.payload.pagination || null;
        state.error = null;
      })
      .addCase(fetchDelegations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Fetch granted delegations
      .addCase(fetchGrantedDelegations.fulfilled, (state, action) => {
        state.grantedDelegations = action.payload.delegations || [];
      })

      // Fetch received delegations
      .addCase(fetchReceivedDelegations.fulfilled, (state, action) => {
        state.receivedDelegations = action.payload.delegations || [];
      })

      // Fetch pending delegations
      .addCase(fetchPendingDelegations.fulfilled, (state, action) => {
        state.pendingDelegations = action.payload.delegations || [];
      })

      // Fetch stats
      .addCase(fetchDelegationStats.fulfilled, (state, action) => {
        state.stats = action.payload.stats || null;
      })

      // Fetch delegable permissions
      .addCase(fetchDelegablePermissions.fulfilled, (state, action) => {
        state.delegablePermissions = action.payload.permissions || [];
      })

      // Create delegation
      .addCase(createDelegation.pending, (state) => {
        state.createStatus = 'loading';
      })
      .addCase(createDelegation.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        const delegation = action.payload.delegation;
        if (delegation.status === 'PENDING') {
          state.pendingDelegations.push(delegation);
        }
        state.grantedDelegations.unshift(delegation);
        state.error = null;
      })
      .addCase(createDelegation.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Approve delegation
      .addCase(approveDelegation.pending, (state) => {
        state.approveStatus = 'loading';
      })
      .addCase(approveDelegation.fulfilled, (state, action) => {
        state.approveStatus = 'succeeded';
        const updated = action.payload.delegation;
        // Remove from pending
        state.pendingDelegations = state.pendingDelegations.filter(d => d.id !== updated.id);
        // Update in other lists
        const updateInList = (list) => {
          const idx = list.findIndex(d => d.id === updated.id);
          if (idx !== -1) list[idx] = updated;
        };
        updateInList(state.delegations);
        updateInList(state.grantedDelegations);
        state.error = null;
      })
      .addCase(approveDelegation.rejected, (state, action) => {
        state.approveStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Reject delegation
      .addCase(rejectDelegation.fulfilled, (state, action) => {
        const updated = action.payload.delegation;
        state.pendingDelegations = state.pendingDelegations.filter(d => d.id !== updated.id);
        const updateInList = (list) => {
          const idx = list.findIndex(d => d.id === updated.id);
          if (idx !== -1) list[idx] = updated;
        };
        updateInList(state.delegations);
        updateInList(state.grantedDelegations);
      })

      // Revoke delegation
      .addCase(revokeDelegation.fulfilled, (state, action) => {
        const updated = action.payload.delegation;
        const updateInList = (list) => {
          const idx = list.findIndex(d => d.id === updated.id);
          if (idx !== -1) list[idx] = updated;
        };
        updateInList(state.delegations);
        updateInList(state.grantedDelegations);
        updateInList(state.receivedDelegations);
      });
  }
});

// =============================================
// SELECTORS
// =============================================

export const selectAllDelegations = (state) => state.delegations?.delegations || [];
export const selectGrantedDelegations = (state) => state.delegations?.grantedDelegations || [];
export const selectReceivedDelegations = (state) => state.delegations?.receivedDelegations || [];
export const selectPendingDelegations = (state) => state.delegations?.pendingDelegations || [];
export const selectDelegablePermissions = (state) => state.delegations?.delegablePermissions || [];
export const selectDelegationStats = (state) => state.delegations?.stats;
export const selectDelegationsStatus = (state) => state.delegations?.status || 'idle';
export const selectDelegationsError = (state) => state.delegations?.error;
export const selectDelegationsPagination = (state) => state.delegations?.pagination;

// Get active delegations I've received
export const selectActiveDelegationsReceived = (state) => {
  const now = new Date();
  return (state.delegations?.receivedDelegations || []).filter(d =>
    d.status === 'ACTIVE' &&
    new Date(d.startsAt) <= now &&
    new Date(d.expiresAt) > now
  );
};

// Get count of pending delegations requiring approval
export const selectPendingDelegationCount = (state) =>
  (state.delegations?.pendingDelegations || []).length;

export const { clearDelegationError, resetDelegationStatuses } = delegationsSlice.actions;

export default delegationsSlice.reducer;
